import { type FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError, apiUrl } from '../api/client';
import {
  type InboxItem,
  type InvoiceMailbox,
  presetFor,
  useCheckMailbox,
  useIgnoreInboxItem,
  useInboxItems,
  useMailboxes,
  useRemoveMailbox,
  useRetryInboxItem,
  useSaveMailbox,
} from '../api/inbox';
import { dt, money, plural, todayIso } from '../lib/format';
import { useUi } from '../state/ui';
import { Btn, Field, Hint, Pill, Surface, SwitchRow } from './ui';

/**
 * Schránka faktur v Přijatých: napojený e-mail, faktury, které z něj čekají na kontrolu,
 * a posledních pár zapsaných. Jisté faktury se zapisují a naskladňují samy.
 */

const STATUS: Record<InboxItem['status'], [string, string]> = {
  new: ['draft', 'čeká'],
  processing: ['draft', 'čtu…'],
  committed: ['paid', 'zapsáno'],
  review: ['partial', 'ke kontrole'],
  duplicate: ['cancelled', 'už zapsaná'],
  ignored: ['cancelled', 'přeskočeno'],
  failed: ['overdue', 'chyba'],
};

const time = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString('cs-CZ', {
        day: 'numeric',
        month: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : 'zatím ne';

export function InboxPanel({ companyId, canWrite }: { companyId: string; canWrite: boolean }) {
  const navigate = useNavigate();
  const { openSheet, closeSheet, toast } = useUi();
  const { data: mailboxes = [], isPending } = useMailboxes(companyId);
  const [showDone, setShowDone] = useState(false);
  const open = useInboxItems(companyId, 'open');
  const done = useInboxItems(showDone ? companyId : null, 'done');
  const check = useCheckMailbox();
  const ignore = useIgnoreInboxItem();
  const retry = useRetryInboxItem();
  const box = mailboxes[0] ?? null;
  const items = open.data?.items ?? [];
  const counts = open.data?.counts ?? {};
  const doneCount = (counts.committed ?? 0) + (counts.duplicate ?? 0) + (counts.ignored ?? 0);

  const edit = (m: InvoiceMailbox | null) =>
    openSheet(
      <MailboxSheet
        companyId={companyId}
        mailbox={m}
        onCancel={closeSheet}
        onDone={(msg) => {
          closeSheet();
          toast(msg);
        }}
      />,
    );

  if (isPending) return null;

  if (!box) {
    return (
      <Surface className="pad inbox-panel">
        <div className="inbox-head">
          <div className="grow">
            <div className="l1">Faktury z e-mailu</div>
            <div className="l2 wrap">
              Napoj e-mail, kam ti dodavatelé posílají faktury. Aplikace ho každých pár minut
              projde, fakturu přečte, zapíše do Přijatých a díly i telefony rovnou naskladní.
            </div>
          </div>
        </div>
        {canWrite && (
          <Btn variant="primary" sm className="mt" onClick={() => edit(null)}>
            Napojit e-mail
          </Btn>
        )}
      </Surface>
    );
  }

  const busy = items.some((i) => i.status === 'new' || i.status === 'processing');

  return (
    <Surface className="pad inbox-panel">
      <div className="inbox-head">
        <div className="grow">
          <div className="l1">
            Faktury z e-mailu{' '}
            {items.length > 0 && (
              <Pill kind="partial">
                {items.length} {plural(items.length, ['ke kontrole', 'ke kontrole', 'ke kontrole'])}
              </Pill>
            )}
          </div>
          <div className="l2 wrap">
            {box.username} ·{' '}
            {box.isActive
              ? box.mode === 'auto'
                ? 'jisté faktury zapisuje sama'
                : 'vše nechává ke kontrole'
              : 'pozastaveno'}{' '}
            · zkontrolováno {time(box.lastCheckAt)}
          </div>
        </div>
        <div className="inbox-actions">
          <Btn
            sm
            loading={check.isPending || busy}
            onClick={() =>
              check.mutate(box.id, {
                onSuccess: (r) =>
                  toast(
                    r.error
                      ? r.error
                      : r.items
                        ? `${r.items} ${plural(r.items, ['nová faktura', 'nové faktury', 'nových faktur'])} – čtu je`
                        : 'Nic nového',
                  ),
                onError: (e) => toast(e instanceof ApiError ? e.message : 'Kontrola selhala'),
              })
            }
          >
            Zkontrolovat
          </Btn>
          {canWrite && (
            <Btn sm onClick={() => edit(box)}>
              Nastavení
            </Btn>
          )}
        </div>
      </div>
      {box.lastError && <p className="warn-box mt">Schránka hlásí chybu: {box.lastError}</p>}

      {items.length > 0 && (
        <div className="inbox-list mt">
          {items.map((i) => (
            <div className="inbox-item" key={i.id}>
              <div className="grow">
                <div className="l1">
                  {i.supplierName || i.fromName || i.fromAddress || i.fileName}
                  {i.externalNumber && <span className="muted"> · {i.externalNumber}</span>}
                </div>
                <div className="l2 wrap">
                  {[
                    dt(i.receivedAt.slice(0, 10)),
                    i.totalAmount ? money(i.totalAmount, i.currency || 'CZK') : '',
                    i.fileName,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </div>
                {i.reason && <div className="l2 wrap inbox-reason">{i.reason}</div>}
              </div>
              <Pill kind={STATUS[i.status][0]}>{STATUS[i.status][1]}</Pill>
              {(i.status === 'review' || i.status === 'failed') && canWrite && (
                <div className="inbox-item-actions">
                  {i.status === 'review' && (
                    <Btn
                      sm
                      variant="primary"
                      onClick={() => navigate(`/doklady/prijata/nova?schranka=${i.id}`)}
                    >
                      Zkontrolovat a zapsat
                    </Btn>
                  )}
                  {i.status === 'failed' && (
                    <Btn
                      sm
                      loading={retry.isPending && retry.variables === i.id}
                      onClick={() => retry.mutate(i.id)}
                    >
                      Zkusit znovu
                    </Btn>
                  )}
                  {i.hasFile && (
                    <a
                      className="btn sm"
                      href={apiUrl(`/api/inbox/items/${i.id}/file`)}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Originál
                    </a>
                  )}
                  <Btn
                    sm
                    variant="ghost"
                    loading={ignore.isPending && ignore.variables === i.id}
                    onClick={() =>
                      ignore.mutate(i.id, { onSuccess: () => toast('Příloha zahozena') })
                    }
                  >
                    Zahodit
                  </Btn>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {!items.length && !box.lastError && (
        <p className="l2 mt">Nic nečeká na kontrolu. Nové faktury se zapíšou samy.</p>
      )}

      {doneCount > 0 && (
        <button type="button" className="link mt" onClick={() => setShowDone((v) => !v)}>
          {showDone ? 'Skrýt vyřízené' : `Vyřízené z e-mailu (${doneCount})`}
        </button>
      )}
      {showDone && (
        <div className="inbox-list mt">
          {(done.data?.items ?? []).slice(0, 20).map((i) => (
            <div className="inbox-item" key={i.id}>
              <div className="grow">
                <div className="l1">
                  {i.supplierName || i.fromName || i.fileName}
                  {i.externalNumber && <span className="muted"> · {i.externalNumber}</span>}
                </div>
                <div className="l2 wrap">
                  {[dt(i.receivedAt.slice(0, 10)), i.reason].filter(Boolean).join(' · ')}
                </div>
              </div>
              {i.document ? (
                <button
                  type="button"
                  className="link"
                  onClick={() => navigate(`/doklady/${i.document?.id}`)}
                >
                  {i.document.number}
                </button>
              ) : (
                <Pill kind={STATUS[i.status][0]}>{STATUS[i.status][1]}</Pill>
              )}
              {i.status === 'ignored' && i.hasFile && canWrite && (
                <Btn sm variant="ghost" onClick={() => retry.mutate(i.id)}>
                  Přečíst znovu
                </Btn>
              )}
            </div>
          ))}
        </div>
      )}
    </Surface>
  );
}

/** Napojení / úprava schránky. Heslo se po uložení už nikdy nezobrazí. */
export function MailboxSheet({
  companyId,
  mailbox,
  onCancel,
  onDone,
}: {
  companyId: string;
  mailbox: InvoiceMailbox | null;
  onCancel: () => void;
  onDone: (msg: string) => void;
}) {
  const save = useSaveMailbox();
  const remove = useRemoveMailbox();
  const [username, setUsername] = useState(mailbox?.username ?? '');
  const [password, setPassword] = useState('');
  const [host, setHost] = useState(mailbox?.host ?? '');
  const [hostTouched, setHostTouched] = useState(!!mailbox);
  const [port, setPort] = useState(String(mailbox?.port ?? 993));
  const [folder, setFolder] = useState(mailbox?.folder ?? 'INBOX');
  const [auto, setAuto] = useState((mailbox?.mode ?? 'auto') === 'auto');
  const [onlyInvoices, setOnlyInvoices] = useState(mailbox?.onlyInvoices ?? true);
  const [active, setActive] = useState(mailbox?.isActive ?? true);
  const [since, setSince] = useState(mailbox?.since ?? '');
  const [more, setMore] = useState(false);
  const preset = presetFor(username);
  const err = save.error instanceof ApiError ? save.error : null;
  const effectiveHost = hostTouched ? host : (preset?.host ?? host);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const common = {
      host: effectiveHost.trim(),
      port: Number(port) || 993,
      username: username.trim(),
      folder: folder.trim() || 'INBOX',
      mode: auto ? ('auto' as const) : ('review' as const),
      onlyInvoices,
      ...(since ? { since } : {}),
    };
    if (mailbox) {
      save.mutate(
        {
          id: mailbox.id,
          patch: {
            ...common,
            isActive: active,
            ...(password ? { password } : {}),
          },
        },
        { onSuccess: () => onDone('Schránka uložena') },
      );
    } else {
      save.mutate(
        { input: { companyId, ...common, password } },
        { onSuccess: () => onDone('E-mail napojen – faktury se budou načítat samy') },
      );
    }
  };

  return (
    <form onSubmit={submit}>
      <h3>{mailbox ? 'Schránka faktur' : 'Napojit e-mail s fakturami'}</h3>
      <p className="muted mb">
        Aplikace se do e-mailu jen dívá (nic nemaže ani neoznačuje jako přečtené). Z příloh s
        fakturou (PDF, ISDOC, fotka) udělá přijatou fakturu a zboží naskladní.
      </p>
      <Field
        id="mb-user"
        label="E-mail"
        type="email"
        autoComplete="off"
        placeholder="faktury@isupply.cz"
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        error={err?.fieldErrors.username}
        required
      />
      <Field
        id="mb-pass"
        label={mailbox ? 'Heslo (nech prázdné, když se nemění)' : 'Heslo'}
        type="password"
        autoComplete="new-password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        error={err?.fieldErrors.password}
        required={!mailbox}
      />
      {preset && <Hint>{preset.hint}</Hint>}
      <div className="two">
        <Field
          id="mb-host"
          label="Server IMAP"
          placeholder="imap.firma.cz"
          value={effectiveHost}
          onChange={(e) => {
            setHostTouched(true);
            setHost(e.target.value);
          }}
          error={err?.fieldErrors.host}
          required
        />
        <Field
          id="mb-port"
          label="Port"
          inputMode="numeric"
          value={port}
          onChange={(e) => setPort(e.target.value)}
        />
      </div>
      <SwitchRow
        title="Jisté faktury zapisovat samy"
        subtitle="Vypnuté = každá faktura počká na tvoje potvrzení"
        checked={auto}
        onChange={setAuto}
      />
      <SwitchRow
        title="Jen e-maily s fakturou"
        subtitle="Přeskočí newslettery s PDF (šetří AI)"
        checked={onlyInvoices}
        onChange={setOnlyInvoices}
      />
      {mailbox && (
        <SwitchRow
          title="Schránka zapnutá"
          subtitle="Vypnutím se kontrola e-mailu pozastaví"
          checked={active}
          onChange={setActive}
        />
      )}
      <button type="button" className="link mb" onClick={() => setMore((v) => !v)}>
        {more ? 'Skrýt podrobnosti' : 'Podrobnosti (složka, od kdy načítat)'}
      </button>
      {more && (
        <div className="two mt">
          <Field
            id="mb-folder"
            label="Složka"
            value={folder}
            onChange={(e) => setFolder(e.target.value)}
          />
          <Field
            id="mb-since"
            label="Načíst e-maily od"
            type="date"
            max={todayIso()}
            value={since}
            onChange={(e) => setSince(e.target.value)}
          />
        </div>
      )}
      {!mailbox && !more && (
        <Hint>Poprvé se načtou e-maily za posledních 14 dní; už zapsané faktury se nezdvojí.</Hint>
      )}
      {err && <p className="form-error">{err.message}</p>}
      <Btn type="submit" variant="primary" full loading={save.isPending}>
        {mailbox ? 'Uložit' : 'Ověřit a napojit'}
      </Btn>
      {mailbox && (
        <Btn
          full
          variant="danger"
          loading={remove.isPending}
          onClick={() =>
            remove.mutate(mailbox.id, { onSuccess: () => onDone('Schránka odpojena') })
          }
        >
          Odpojit schránku
        </Btn>
      )}
      <Btn full onClick={onCancel}>
        Zrušit
      </Btn>
    </form>
  );
}
